import React, { useEffect, useLayoutEffect, useRef, useState, CSSProperties } from 'react';
// Grid stays v4 on purpose: both roots are Grid items inside a caller's v4 Grid container, which spaces them
// through its .MuiGrid-item rule. A v9 or plain root would lose that padding on every caller; move it with them.
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import Grid from './LegacyGrid';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider, DesktopDatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatesOptionsSelect } from '.';
import { DateRange, getISOWeekNumber } from '../utils/date-range';

interface Props {
  onChange: (fromDate: Date, toDate: Date) => void;
  onInitialized?: (defaultRange: DateRange) => void;
  onPresetSelected?: (name: string | null) => void;
  fullWidth?: boolean;
  isInjectionSearch?: boolean;
  isHourlyFormat?: boolean;
  /** enable week selection (default true) */
  withWeekSelect?: boolean;
  /** how many months back the default 'from' should start (default 1 = current month) */
  monthsBack?: number;
  /** allow selecting future dates in pickers (default false) */
  allowFuture?: boolean;
  /** use end of day (23:59:59) instead of current timestamp for the default end date (default false) */
  useEndOfDay?: boolean;
  /** override the initial from date (e.g. from saved filters) */
  initialFromDate?: Date | null;
  /** override the initial to date (e.g. from saved filters) */
  initialToDate?: Date | null;
}

const paperStyle: CSSProperties = { maxHeight: 40 * 6.5, overflowY: 'auto' };
const SELECT_MENU_PROPS = {
  MenuProps: {
    disableScrollLock: true,
    // v4 opened these menus over the select, top-left aligned (getContentAnchorEl: null); v9 dropped that option.
    anchorOrigin: { vertical: 'top', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    slotProps: { paper: { style: paperStyle } }
  }
} as const;

const getISOWeekStart = (week: number, year: number): Date => {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const monday = new Date(simple);
  monday.setDate(simple.getDate() - ((dow + 6) % 7));
  monday.setHours(0, 0, 0, 0);
  return monday;
};

const DateWeekRangeSelector: React.FC<Props> = ({
  onChange,
  onInitialized,
  onPresetSelected,
  fullWidth,
  isInjectionSearch,
  isHourlyFormat,
  withWeekSelect = true,
  monthsBack,
  allowFuture = false,
  useEndOfDay = false,
  initialFromDate,
  initialToDate
}) => {
  const { t } = useTranslation();
  const sourceRef = useRef<'date' | 'week' | null>(null);
  const hasInitializedRef = useRef(false);

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentWeek = getISOWeekNumber(now);
  const backMonths = Math.max(0, monthsBack ?? 1); // ensure non-negative

  // start from the first day of the (now - monthsBack + 1) month
  const startOfPeriod = new Date(now);
  startOfPeriod.setMonth(now.getMonth() - backMonths + 1, 1);
  startOfPeriod.setHours(0, 0, 0, 0);

  // end of current month (or today if we're not yet there)
  const endOfPeriod = new Date(now);
  endOfPeriod.setMonth(now.getMonth() + 1, 0);
  endOfPeriod.setHours(23, 59, 59, 999);

  // never go beyond today
  const cap = useEndOfDay
    ? (() => {
        const d = new Date(now);
        d.setHours(23, 59, 59, 999);
        return d;
      })()
    : now;
  const safeEnd = !allowFuture && endOfPeriod > cap ? cap : endOfPeriod;

  // compute week numbers safely
  const defaultStart = getISOWeekNumber(startOfPeriod);
  const defaultEnd = getISOWeekNumber(safeEnd);

  const defaultRange: DateRange = {
    from: new Date(startOfPeriod),
    to: new Date(safeEnd)
  };

  const effectiveFrom = initialFromDate ?? defaultRange.from;
  const effectiveTo = initialToDate ?? defaultRange.to;

  const [year, setYear] = useState(initialFromDate ? initialFromDate.getFullYear() : currentYear);
  const [startWeek, setStartWeek] = useState(initialFromDate ? getISOWeekNumber(initialFromDate) : defaultStart);
  const [endWeek, setEndWeek] = useState(initialToDate ? getISOWeekNumber(initialToDate) : defaultEnd);
  const [fromDate, setFromDate] = useState(effectiveFrom);
  const [toDate, setToDate] = useState(effectiveTo);

  const years = Array.from({ length: 20 }, (_, i) => currentYear - i);
  const weeks = Array.from({ length: 53 }, (_, i) => i + 1);
  const isFutureWeek = (w: number) => year === currentYear && w > currentWeek;

  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);

  // Initialize parent with default values on first mount
  useLayoutEffect(() => {
    if (!hasInitializedRef.current && onInitialized) {
      hasInitializedRef.current = true;
      onInitialized(defaultRange);
    }
  }, []);

  useEffect(() => {
    if (sourceRef.current === 'week') {
      const from = getISOWeekStart(startWeek, year);
      const to = getISOWeekStart(endWeek, year);
      to.setDate(to.getDate() + 6);
      to.setHours(23, 59, 59, 999);
      setFromDate(from);
      setToDate(to);
      onChange(from, to);
      sourceRef.current = null;
    }
  }, [year, startWeek, endWeek]);

  useEffect(() => {
    if (sourceRef.current === 'date') {
      if (fromDate > toDate) return;
      const newYear = fromDate.getFullYear();
      const newStart = getISOWeekNumber(fromDate);
      const newEnd = getISOWeekNumber(toDate);
      setYear(newYear);
      setStartWeek(newStart);
      setEndWeek(newEnd);
      onChange(fromDate, toDate);
      sourceRef.current = null;
    }
  }, [fromDate, toDate]);

  const handleDateSelect = (option: { dateFrom: Date | null; dateTo: Date | null; name?: string }) => {
    if (option.dateFrom && option.dateTo) {
      sourceRef.current = 'date';
      setFromDate(option.dateFrom);
      setToDate(option.dateTo);
      onPresetSelected?.(option.name ?? null);
    }
  };

  return (
    <>
      <Grid item container direction="row" alignItems="center" spacing={1}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid item md={3} sm={4} xs={12}>
            {(isHourlyFormat && (
              <DateTimePicker
                format="dd/MM/yyyy HH:mm"
                label={t('f-search-date-to')}
                value={fromDate}
                onChange={(date) => {
                  if (date) {
                    sourceRef.current = 'date';
                    setFromDate(date as Date);
                    onPresetSelected?.(null);
                  }
                }}
                slotProps={{ textField: { fullWidth: true, margin: 'normal', id: 'dateFromSelect' } }}
              />
            )) || (
              <DesktopDatePicker
                format={'dd/MM/yyyy'}
                label={isInjectionSearch ? t('f-search-date-from-inj') : t('f-search-date-from')}
                value={fromDate}
                onChange={(date) => {
                  if (date) {
                    sourceRef.current = 'date';
                    setFromDate(date as Date);
                    onPresetSelected?.(null);
                  }
                }}
                slotProps={{ textField: { fullWidth: true, margin: 'normal', id: 'dateFromSelect' } }}
              />
            )}
          </Grid>

          <Grid item md={3} sm={4} xs={12}>
            {(isHourlyFormat && (
              <DateTimePicker
                format="dd/MM/yyyy HH:mm"
                label={t('f-search-date-to')}
                value={toDate}
                maxDateTime={
                  allowFuture
                    ? undefined
                    : (() => {
                        const d = new Date();
                        d.setHours(23, 59, 59, 999);
                        return d;
                      })()
                }
                onChange={(date) => {
                  if (date) {
                    sourceRef.current = 'date';
                    setToDate(date as Date);
                    onPresetSelected?.(null);
                  }
                }}
                slotProps={{ textField: { fullWidth: true, margin: 'normal', id: 'dateFromSelect' } }}
              />
            )) || (
              <DesktopDatePicker
                format={'dd/MM/yyyy'}
                label={isInjectionSearch ? t('f-search-date-to-inj') : t('f-search-date-to')}
                value={toDate}
                maxDate={allowFuture ? undefined : todayEnd}
                onChange={(date) => {
                  if (date) {
                    sourceRef.current = 'date';
                    setToDate(date as Date);
                    onPresetSelected?.(null);
                  }
                }}
                slotProps={{ textField: { fullWidth: true, margin: 'normal', id: 'dateToSelect' } }}
              />
            )}
          </Grid>
        </LocalizationProvider>

        <Grid item md={2} sm={4} xs={12}>
          <DatesOptionsSelect onSelect={handleDateSelect} allowFuture={allowFuture} />
        </Grid>
      </Grid>

      {withWeekSelect && (
        <Grid item container direction="row" alignItems="center" spacing={1}>
          <Grid item md={3} sm={4} xs={12}>
            <FormControl fullWidth={fullWidth} variant="outlined" size="small">
              <InputLabel id="from-week-label">{t('f-com-week-from')}</InputLabel>
              <Select
                {...SELECT_MENU_PROPS}
                labelId="from-week-label"
                value={startWeek}
                onChange={(e) => {
                  sourceRef.current = 'week';
                  setStartWeek(Number(e.target.value));
                  onPresetSelected?.(null);
                }}
                label={t('f-com-week-from')}
              >
                {weeks.map((w) => (
                  <MenuItem key={w} value={w} disabled={isFutureWeek(w) || w > endWeek}>
                    {`${t('f-com-week')} ${w}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={3} sm={4} xs={12}>
            <FormControl fullWidth={fullWidth} variant="outlined" size="small">
              <InputLabel id="from-week-label">{t('f-com-week-to')}</InputLabel>
              <Select
                {...SELECT_MENU_PROPS}
                labelId="from-week-label"
                value={endWeek}
                onChange={(e) => {
                  sourceRef.current = 'week';
                  setEndWeek(Number(e.target.value));
                  onPresetSelected?.(null);
                }}
                label={t('f-com-week-to')}
              >
                {weeks.map((w) => (
                  <MenuItem key={w} value={w} disabled={isFutureWeek(w) || w < startWeek}>
                    {`${t('f-com-week')} ${w}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={2} sm={4} xs={12}>
            <FormControl fullWidth={fullWidth} variant="outlined" size="small">
              <InputLabel id="year-label">{t('f-com-year')}</InputLabel>
              <Select
                {...SELECT_MENU_PROPS}
                labelId="year-label"
                value={year}
                onChange={(e) => {
                  sourceRef.current = 'week';
                  const y = Number(e.target.value);
                  setYear(y);
                  if (y === currentYear) {
                    if (startWeek > currentWeek) setStartWeek(currentWeek);
                    if (endWeek > currentWeek) setEndWeek(currentWeek);
                  }
                  onPresetSelected?.(null);
                }}
                label={t('f-com-year')}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default DateWeekRangeSelector;
